import React, { useState, useEffect } from 'react';
import { FaChevronRight, FaChevronDown } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

const formatSize = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const downloadUrl = (version) => `api/software/android/${version}`;

// The notes, product name, intro and footnote come from releases.json on S3 and are shown
// as written there; only this page's own wording is translated.
const ReleaseNotes = ({ notes }) => (
    <ul className="mb-0">
        {(notes || []).map((note, index) => (
            <li key={index}>
                {note.title && <strong>{note.title}</strong>}
                {note.title && note.text ? ' ' : ''}
                {note.text}
            </li>
        ))}
    </ul>
);

export const AndroidUpdates = () => {
    const { t } = useTranslation();
    const [manifest, setManifest] = useState(null);
    const [error, setError] = useState(null);
    const [expanded, setExpanded] = useState(null);

    useEffect(() => {
        let cancelled = false;
        fetch('api/software/android/releases')
            .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
            .then(data => { if (!cancelled) setManifest(data); })
            .catch(err => { if (!cancelled) setError(err.message); });
        return () => { cancelled = true; };
    }, []);

    const releases = (manifest && manifest.releases) || [];
    const latest = releases.find(r => r.version === (manifest && manifest.latest)) || releases[0];
    const previous = releases.filter(r => r !== latest);

    const toggle = (version) => setExpanded(expanded === version ? null : version);

    return (
        <div className="container py-4">
            <div className="d-flex align-items-baseline mb-3">
                <h2 className="mb-0">{(manifest && manifest.product) || t('downloads.androidProduct')}</h2>
                <span className="text-muted ms-3 small">
                    {releases.length > 0 ? t('downloads.releaseCount', { count: releases.length }) : ''}
                </span>
            </div>
            <p className="text-muted">
                {(manifest && manifest.intro) || t('downloads.androidIntro')}
            </p>

            {error && (
                <div className="alert alert-danger" role="alert">
                    {t('downloads.releaseListFailed', { error })}
                </div>
            )}

            {!manifest && !error && (
                <p><em>{t('downloads.loading')}</em></p>
            )}

            {latest && (
                <div className="card shadow-sm mb-4">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h4 className="mb-0">
                            {t('downloads.version', { version: latest.version })}
                            <span className="badge bg-light text-dark ms-2">{t('downloads.latest')}</span>
                        </h4>
                        <span className="small" style={{ opacity: 0.85 }}>{latest.released}</span>
                    </div>
                    <div className="card-body">
                        <ReleaseNotes notes={latest.notes} />
                        <a href={downloadUrl(latest.version)} className="btn btn-light mt-3">
                            {t('downloads.downloadApk')}
                        </a>
                        <span className="small ms-2" style={{ opacity: 0.85 }}>{formatSize(latest.sizeBytes)}</span>
                    </div>
                </div>
            )}

            {previous.length > 0 && (
                <>
                    <h5>{t('downloads.previousReleases')}</h5>
                    <table className="table table-sm align-middle mb-4">
                        <thead>
                            <tr>
                                <th style={{ width: 32 }}></th>
                                <th>{t('downloads.columnVersion')}</th>
                                <th>{t('downloads.columnReleased')}</th>
                                <th>{t('downloads.columnSize')}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {previous.map(release => {
                                const isOpen = expanded === release.version;
                                return (
                                    <React.Fragment key={release.version}>
                                        <tr onClick={() => toggle(release.version)} style={{ cursor: 'pointer' }}>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="btn btn-link btn-sm p-0"
                                                    aria-expanded={isOpen}
                                                    aria-label={t(isOpen ? 'downloads.hideNotes' : 'downloads.showNotes', { version: release.version })}
                                                    onClick={(e) => { e.stopPropagation(); toggle(release.version); }}
                                                >
                                                    {isOpen ? <FaChevronDown /> : <FaChevronRight />}
                                                </button>
                                            </td>
                                            <td>{release.version}</td>
                                            <td>{release.released}</td>
                                            <td>{formatSize(release.sizeBytes)}</td>
                                            <td className="text-end">
                                                <a
                                                    href={downloadUrl(release.version)}
                                                    className="btn btn-primary btn-sm"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {t('downloads.downloadApk')}
                                                </a>
                                            </td>
                                        </tr>
                                        {isOpen && (
                                            <tr>
                                                <td></td>
                                                <td colSpan={4}>
                                                    <ReleaseNotes notes={release.notes} />
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </>
            )}

            {manifest && manifest.toolSupport && manifest.toolSupport.length > 0 && (
                <>
                    <h5>{t('downloads.toolSupport')}</h5>
                    <table className="table table-sm w-auto">
                        <thead>
                            <tr><th>{t('downloads.columnTool')}</th><th>{t('downloads.columnSupportedFrom')}</th></tr>
                        </thead>
                        <tbody>
                            {manifest.toolSupport.map(support => (
                                <tr key={support.tool}>
                                    <td>{support.tool}</td>
                                    <td>{support.from}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}

            {manifest && manifest.footnote && (
                <p className="text-muted small">{manifest.footnote}</p>
            )}

            <h5 className="mt-4">{t('downloads.wallpaperHeading')}</h5>
            <p className="text-muted mb-2">{t('downloads.wallpaperIntro')}</p>
            <a href="api/software/android/wallpaper" className="btn btn-primary btn-sm">
                {t('downloads.downloadWallpaper')}
            </a>
        </div>
    );
}
